// Pi relay: StreamTape playback and subtitles from our own box.
//
// StreamTape hands out a signed PROGRESSIVE mp4 the browser can play directly,
// and that is what HostedFileProvider has returned since 2026-09-26. The relay
// (web/poku-stream-relay.mjs, port 8789) fronts the same file and adds what the
// CDN has no way to:
//
//   - SUBTITLES. StreamTape flattens every upload, so the file itself carries
//     none, and the public Blobs store does not have a track for every mapped
//     title (checked 2026-10-01: /api/subs/get for Padavettu answers with zero
//     tracks while the Pi's own store holds the English one). The relay serves
//     them out of that store, with release-scene advert cues removed at serve
//     time rather than in the stored file.
//   - A URL that does not expire. The signature lives ~24 h and has to be
//     minted per playback; <base>/video?id=<fileId> is derived, not minted.
//
// Nothing here runs unless config.piRelayBase is set. With it empty — the
// default — every function returns null and playback is byte-for-byte the
// pre-relay path, which is the point: this is a switch, not a migration.

/**
 * The StreamTape file id for a resolved hosted mp4.
 *
 * `probe.fileId` is the real answer and the deployed backend already sends it.
 * The local copies of server.mjs / netlify api.mjs were behind on that field
 * (fixed alongside this), and a self-hosted backend may be older still, so the
 * signed URL is a fallback source: it has the shape
 *
 *   https://<node>.tapecontent.net/radosgw/<fileId>/<signature>/<name>.mp4
 *
 * and the segment after /radosgw/ is the id (verified live 2026-10-01 against
 * a7kzGbMbwWFxV8g). Treated strictly as a fallback and matched narrowly: if
 * their path layout changes this returns null and playback simply stays on the
 * direct CDN URL instead of building a relay URL out of a wrong guess.
 */
export function streamtapeFileId(probe) {
  const direct = String(probe?.fileId || "").trim();
  if (/^[A-Za-z0-9_-]{6,40}$/.test(direct)) return direct;

  const url = String(probe?.mp4Url || "");
  if (!url) return null;
  let path = "";
  try {
    path = new URL(url).pathname;
  } catch {
    return null;
  }
  const m = path.match(/^\/radosgw\/([A-Za-z0-9_-]{6,40})\//);
  return m ? m[1] : null;
}

/** `<base>/video?id=<fileId>`, or null when the relay is off / id unknown. */
export function relayVideoUrl(base, fileId) {
  if (!base || !fileId) return null;
  if (!/^[A-Za-z0-9_-]{6,40}$/.test(String(fileId))) return null;
  return `${base}/video?id=${encodeURIComponent(fileId)}`;
}

/** The relay's subtitle INDEX url for a playback request, or null. */
export function relaySubsUrl(base, request) {
  if (!base || !request?.tmdbId) return null;
  const episode = request.contentType === "EPISODE";
  if (episode && (request.season == null || request.episode == null)) return null;
  const q = new URLSearchParams({
    type: episode ? "episode" : "movie",
    id: String(request.tmdbId),
  });
  if (episode) {
    q.set("season", String(request.season));
    q.set("episode", String(request.episode));
  }
  return `${base}/subs?${q.toString()}`;
}

const defaultToObjectUrl = (text) =>
  URL.createObjectURL(new Blob([text], { type: "text/vtt" }));

const defaultRevoke = (url) => URL.revokeObjectURL(url);

/**
 * Fetch the relay's tracks for a title as <track>-ready sources.
 *
 * The bodies are fetched and wrapped in blob: URLs rather than pointed at
 * directly, and that is deliberate. A cross-origin <track src> only loads if
 * the <video> carries crossorigin="anonymous" — which would then put the VIDEO
 * request under a CORS check too, and the relay's /video deliberately forwards
 * no Access-Control-Allow-Origin (it is a byte relay; changing its headers was
 * out of scope and unnecessary). A blob: URL is same-origin, so the tracks
 * attach with the <video> left exactly as it is. /subs does send ACAO: *, so
 * the fetch itself is fine.
 *
 * Returns { tracks, revoke }. The caller MUST call revoke() on unmount or the
 * blobs leak for the lifetime of the document.
 */
export async function loadRelaySubtitles(
  base,
  request,
  { fetchImpl = fetch, toObjectUrl = defaultToObjectUrl, revokeUrl = defaultRevoke, signal = null } = {},
) {
  const indexUrl = relaySubsUrl(base, request);
  if (!indexUrl) return { tracks: [], revoke: () => {} };

  const r = await fetchImpl(indexUrl, signal ? { signal } : undefined);
  if (!r.ok) throw new Error(`relay subtitles: HTTP ${r.status}`);
  const index = await r.json();
  const list = Array.isArray(index?.tracks) ? index.tracks : [];

  const created = [];
  const tracks = [];
  for (const t of list) {
    if (!t?.url) continue;
    // The relay returns relative urls ("/subs/track?…"), which is what keeps
    // filesystem paths out of them — resolve against the configured base.
    const body = await fetchImpl(`${base}${t.url}`, signal ? { signal } : undefined);
    if (!body.ok) continue;
    const url = toObjectUrl(await body.text());
    created.push(url);
    tracks.push({ id: t.id, lang: t.lang || "", label: t.label || t.lang || "Subtitle", url });
  }

  return {
    tracks,
    revoke: () => {
      for (const url of created) {
        try {
          revokeUrl(url);
        } catch {
          /* already revoked, or a document being torn down */
        }
      }
      created.length = 0;
    },
  };
}
